import React from 'react';
import * as mobx from 'mobx';
import { FormattedMessage, IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IAccountDetailsDTO, IAccountsDAO } from '../../../api/accounts.dao';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { alertServerError, showNotification } from '../../global/modal/alerts-host/alert-templates';
import { IAlertsService } from '../../../infrastructure/alerts';
import { IEventsDAO } from '../../../api/events.dao';
import { Space } from '../../../constants/html-character-entities';
import { SortOrder } from '../../../api/shared.dto';
import { AccountFormDataVM } from './account-form-data.viewmodel';
import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';
import { convertBoolTypeForMixPanel, TRACKING_EVENTS } from '../../../tracking-service/tracking-values';

class AccountFormVM {
    @mobx.observable
    public accountId: number;

    @mobx.observable
    private isFormDataReady: boolean;

    @mobx.observable
    private accountFormDataVM: AccountFormDataVM;

    @mobx.observable
    private reservedSubdomains: string[];

    @mobx.observable
    public eventsCount: number;

    @mobx.observable
    private saving: boolean;

    @mobx.observable
    private cachedAccountDetailsDTO: IAccountDetailsDTO;

    constructor(
        private fetchAccountsList: () => void,
        private closeEditAccountForm: (accountId: string) => void,
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider)
    ) { }

    public isSaving() {
        return this.saving;
    }

    public getFormDataVM(): AccountFormDataVM {
        return this.accountFormDataVM;
    }

    public getIsFormDataReady(): boolean {
        return this.isFormDataReady;
    }

    public getErrorMessage(intl: IntlShape): string | null {
        if (this.getFormDataVM().showValidationErrors() && this.getFormDataVM().getNameError(intl)) {
            return this.getFormDataVM().getNameError(intl);
        }
        return this.getFormDataVM().getServerErrorMessage();
    }

    @mobx.action
    public async setAccount(accountId?: number): Promise<void> {
        if (accountId !== this.accountId) {
            this.accountId = accountId;

            if (this.accountId) {
                await this.fetchFormData();
            } else {
                this.setEmptyFormData();
            }
        }
    }

    @mobx.action
    public initializeParams(subdomains: string[]): void {
        this.reservedSubdomains = subdomains;
        this.setEmptyFormData();
    }

    public onDeleteAccount = () => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.alerts.areYouSure'
                        description='Alert message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.alerts.deleteThisAccount'
                            description='Alert message in delete account popup window'
                            defaultMessage='delete this account?'
                        />
                    </span>
                    <Space />
                    <FormattedMessage
                        id='oe.alerts.deleteAccount.afterDeletion'
                        description='Alert message in delete account popup window second part'
                        defaultMessage='After deletion,'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.alerts.deleteAccount.thisDataCannotBeRestored'
                            description='Alert message in delete account popup window second part'
                            defaultMessage='this data cannot be restored.'
                        />
                    </span>
                </span>
            ),
            confirmButtonProps: {
                text: <FormattedMessage
                    id='oe.accounts.deleteAccount'
                    description='Button label'
                    defaultMessage='Delete Account'
                />,
                modifier: 'confirm-warn',
                handler: this.deleteAccount,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    public fetchEventsCount = async () => {
        const eventsFetchResult = await IoC.Container.get<IEventsDAO>(IEventsDAO).getEvents({
            accountId: this.accountId,
            size: 1,
            page: 0,
            sortOrder: SortOrder.Asc,
            sortField: 'startDateTime'
        });

        this.fetchEventsComplete(eventsFetchResult.totalElements);
    };

    @mobx.action
    private fetchEventsComplete = count => {
        this.eventsCount = count;
    };

    @mobx.action
    private async fetchFormData(): Promise<void> {
        this.isFormDataReady = false;
        try {
            const accountInfo = await this.accountsDAO.readAccount(this.accountId);
            this.onFetchAccountSuccess(accountInfo);
        } catch (error) {
            this.onFetchAccountFail(error);
        }
    }

    @mobx.action
    private onFetchAccountSuccess(accountDetails: IAccountDetailsDTO): void {
        this.isFormDataReady = true;
        this.cachedAccountDetailsDTO = accountDetails;
        this.accountFormDataVM = new AccountFormDataVM(accountDetails);
        this.fetchEventsCount();
    }

    private onFetchAccountFail(error): void {
        const errorMessage = this.errorParsingService.extractMessage(error);
        alertServerError('Error', errorMessage);
    }

    @mobx.action
    public handleSubmit = async (intl: IntlShape) => {
        this.getFormDataVM().setServerErrorMessage('');
        if (!this.getFormDataVM().isFormValid(intl)) {
            this.getFormDataVM().allowToShowValidationErrors();
            return false;
        }

        try {
            this.saving = true;
            const name = this.getFormDataVM().getName()?.trim() || null;
            const salesforceAccountId = this.getFormDataVM().getSalesforceAccountID()?.trim() || null;
            const allowedProviders = this.getFormDataVM().getAllowedProviders()?.split('\n')
                .map((el: string) => el.trim()).filter(el => el.length) || [];
            if (this.accountId) {
                await this.accountsDAO.updateAccount(this.getFormDataVM().toApiAccountDTO());
                this.sendTrackingData(TRACKING_EVENTS.EDIT_ACCOUNT, {
                    'Organization Name': convertBoolTypeForMixPanel(!(name === this.cachedAccountDetailsDTO.name)),
                    'Salesforce Account ID': convertBoolTypeForMixPanel(!(salesforceAccountId === this.cachedAccountDetailsDTO.salesforceAccountId)),
                    'Passport SSO Identity Providers': convertBoolTypeForMixPanel(!(allowedProviders.join('') === this.cachedAccountDetailsDTO.allowedProviders.join(''))),
                });
                showNotification(
                    intl.formatMessage({
                        id: 'oe.accounts.accountIsUpdated',
                        description: 'Notification about updated account',
                        defaultMessage: 'Account is updated',
                    }));
            }

            else {
                await this.accountsDAO.createAccount(
                    name,
                    salesforceAccountId,
                    allowedProviders
                );
                this.sendTrackingData(TRACKING_EVENTS.NEW_ACCOUNT, {
                    'Organization Name': convertBoolTypeForMixPanel(!!name),
                    'Salesforce Account ID': convertBoolTypeForMixPanel(!!salesforceAccountId),
                    'Passport SSO Identity Providers': convertBoolTypeForMixPanel(!!allowedProviders.length),
                });
                showNotification(
                    intl.formatMessage({
                        id: 'oe.accounts.accountAdded',
                        description: 'Notification about added account',
                        defaultMessage: 'New account added',
                    }));
            }
            this.fetchAccountsList();
            return true;
        } catch (error) {
            this.onCreateAccountError(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });

        }
    };

    private onCreateAccountError(error): void {
        let errorMessage: string;
        errorMessage = this.errorParsingService.extractMessage(error);
        if (errorMessage) {
            this.getFormDataVM().setServerErrorMessage(errorMessage);
        }
    }

    private deleteAccount = async () => {
        try {
            await this.accountsDAO.deleteAccount({
                accountId: this.accountId,
            });
            this.closeEditAccountForm(this.accountId.toString(10));
            this.trackingService.get().track(TRACKING_EVENTS.DELETE_ACCOUNT, {});
            this.fetchAccountsList();
        } catch (error) {
            this.fetchEventsCount();
            alertServerError('Error', this.errorParsingService.extractMessage(error));
        }
    };

    private setEmptyFormData() {
        this.accountFormDataVM = new AccountFormDataVM({
            accountId: null,
            name: '',
            salesforceAccountId: '',
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private sendTrackingData(event: TRACKING_EVENTS, props: { [key: string]: any }): void {
        this.trackingService.get().track(event, props);
    }

}

export { AccountFormVM };
